'use client';

import { Edit3, Trash2 } from "lucide-react"
import { Link } from '@tanstack/react-router'
import { Button } from '@city-sage/aurum-avis-labs-components'
import { Day, StopStateEnum } from "@city-sage/core-client"
import { StopPreviewCard } from "./StopPreviewCard"
import { DAY_COLORS } from "../lib/constants/dayColors"
import { useDayStops } from "../hooks/useDayStops";
import { MarkdownRenderer } from "./MarkdownRenderer";

interface ItineraryDayCardProps {
  day: Day
  itineraryId: string
}

export function ItineraryDayCard({
  day,
  itineraryId,
}: ItineraryDayCardProps) {

  const { data: stops, isLoading: isLoadingStops } = useDayStops({
    itineraryId,
    dayId: day.id,
    states: [StopStateEnum.Accepted, StopStateEnum.Suggested]
  })


  return (
    <div className="pb-6 border-b border-gray-100 last:border-b-0">
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`min-h-10 min-w-10 rounded-full ${DAY_COLORS[day.number - 1].bgClass} text-primary-foreground font-bold flex items-center justify-center`}>
              {day.number}
            </div>
            <div>
              <h4 className="font-semibold">{day.title || `Day ${day.number}`}</h4>
              <p className="text-sm text-gray-600">{stops?.length || 0} stops planned</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/itineraries/${itineraryId}/days/${day.id}/edit`}>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0 text-gray-400 hover:text-gray-600">
                <Edit3 className="h-3 w-3" />
                <span className="sr-only">Edit day</span>
              </Button>
            </Link>
          </div>
        </div>
        
        {day.description && (
          <div className="mt-3 text-sm text-gray-600">
            <MarkdownRenderer content={day.description} className="text-sm text-gray-600" />
          </div>
        )}

        {/* Stops preview */}
        {stops && stops.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {stops.map((stop, stopIndex) => (
              <StopPreviewCard
                key={stop.id}
                stop={stop}
                stopNumber={stopIndex + 1}
                itineraryId={itineraryId}
                dayId={day.id}
              />
            ))}
          </div>
        )}
        {(!stops || stops.length === 0) && (
          <div className="mt-4 p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
            <p className="text-sm text-gray-500">No stops added yet</p>
          </div>
        )}
      </div>
    </div>
  )
} 